import { useCallback, useEffect, useRef } from 'react'
import { toast } from '../lib/shadcn/sonner'
import { useRestoreDeletedItem } from './backend/settingsPage'

// Deletes copy what they remove into Recently deleted (Settings) and return
// its trash_id. showUndo shows the "deleted" toast with an Undo button that
// puts it back. A restore (from here or from Settings) fires
// ITEM_RESTORED_EVENT with the item's kind so open pages can reload.

export const ITEM_RESTORED_EVENT = 'hub:item-restored'

export type RestoredDetail = { kind: string; id: number }

type RestoreResult = { kind: string; label: string; restored: number; skipped: number }

const UNDO_MS = 10_000

export function useRestoreItem() {
  const restore = useRestoreDeletedItem()
  const trigger = useRef(restore.trigger)
  trigger.current = restore.trigger

  return useCallback(async (id: number): Promise<RestoreResult | null> => {
    try {
      const r = (await trigger.current({ id })) as RestoreResult
      window.dispatchEvent(
        new CustomEvent<RestoredDetail>(ITEM_RESTORED_EVENT, { detail: { kind: r.kind, id } })
      )
      if (r.skipped > 0) {
        toast.warning(
          `Restored ${r.label}, but ${r.skipped} part${r.skipped === 1 ? '' : 's'} already existed again and ${r.skipped === 1 ? 'was' : 'were'} left as is`,
          { duration: 10_000 }
        )
      } else {
        toast.success(`Restored ${r.label}`)
      }
      return r
    } catch (e) {
      toast.error(e instanceof Error && e.message ? `Couldn't restore: ${e.message}` : "Couldn't restore")
      return null
    }
  }, [])
}

export function useUndoToast() {
  const restore = useRestoreItem()
  return useCallback(
    (message: string, trashId: number | null | undefined) => {
      if (!trashId) {
        toast.success(message)
        return
      }
      toast.success(message, {
        duration: UNDO_MS,
        description: 'Also in Settings › Recently deleted for 30 days.',
        action: { label: 'Undo', onClick: () => void restore(trashId) },
      })
    },
    [restore]
  )
}

// Calls onRestored when an item of one of these kinds is put back.
export function useOnRestored(kinds: string[], onRestored: () => void) {
  const cb = useRef(onRestored)
  cb.current = onRestored
  const key = kinds.join(',')
  useEffect(() => {
    const wanted = key.split(',')
    const handler = (e: Event) => {
      const kind = (e as CustomEvent<RestoredDetail>).detail?.kind
      if (kind && wanted.includes(kind)) cb.current()
    }
    window.addEventListener(ITEM_RESTORED_EVENT, handler)
    return () => window.removeEventListener(ITEM_RESTORED_EVENT, handler)
  }, [key])
}
